/**
 * Entitlements Hook and Context (Feature #15)
 * 
 * Provides tenant-scoped feature entitlements for frontend UI control.
 * Features are loaded from backend on mount and cached.
 * 
 * Usage:
 * 1. Wrap app in EntitlementsProvider
 * 2. Use useEntitlements() hook to access feature flags
 * 
 * @example
 * // In App.jsx
 * import { EntitlementsProvider } from './hooks/useEntitlements';
 * 
 * function App() {
 *   return (
 *     <EntitlementsProvider>
 *       <YourApp />
 *     </EntitlementsProvider>
 *   );
 * }
 * 
 * // In a component
 * import { useEntitlements } from '../hooks/useEntitlements';
 * 
 * function ExportButton() {
 *   const { isFeatureEnabled, loading } = useEntitlements();
 *   
 *   if (loading) return <Spinner />;
 *   if (!isFeatureEnabled('INVOICE_EXPORT_CSV')) return null;
 *   
 *   return <button>Export CSV</button>;
 * }
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchEntitlements } from '../services/api';

// ============================================================================
// FEATURE CONSTANTS
// ============================================================================

/**
 * Feature flag constants matching backend Feature enum.
 * Use these to check feature availability.
 */
export const Features = Object.freeze({
  INVOICE_PDF_DOWNLOAD:  'INVOICE_PDF_DOWNLOAD',
  INVOICE_HISTORY:       'INVOICE_HISTORY',
  INVOICE_EXPORT_CSV:    'INVOICE_EXPORT_CSV',
  INVOICE_EMAIL:         'INVOICE_EMAIL',
  INVOICE_SHARE:         'INVOICE_SHARE',
  INVOICE_BULK_DOWNLOAD: 'INVOICE_BULK_DOWNLOAD',
  INVOICE_DRAFT_SAVE:    'INVOICE_DRAFT_SAVE',
});

// ============================================================================
// CONTEXT
// ============================================================================

const EntitlementsContext = createContext({
  features: {},
  loading: true,
  error: null,
  tenantId: null,
  plan: 'free',
  monthlyUsed: 0,
  monthlyLimit: 5,
  isProUser: false,
  isFeatureEnabled: () => false,
  refresh: () => Promise.resolve(),
});

// ============================================================================
// PROVIDER
// ============================================================================

/**
 * Entitlements Provider Component
 * 
 * Fetches entitlements on mount and provides them to child components.
 * Should be placed near the root of the app, but after any auth provider.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
const GUEST_FEATURES = Object.freeze(
  Object.fromEntries(Object.values(Features).map(f => [f, false]))
);

export function EntitlementsProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [features, setFeatures]       = useState({});
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [tenantId, setTenantId]       = useState(null);
  const [plan, setPlan]               = useState('free');
  const [monthlyUsed, setMonthlyUsed] = useState(0);
  const [monthlyLimit, setMonthlyLimit] = useState(5);

  /**
   * Load entitlements from backend (only when a user is logged in)
   */
  const loadEntitlements = useCallback(async () => {
    if (!user) {
      setFeatures(GUEST_FEATURES);
      setTenantId(null);
      setPlan('free');
      setMonthlyUsed(0);
      setMonthlyLimit(5);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetchEntitlements();
      setFeatures(response.features || {});
      setTenantId(response.tenant_id);
      setPlan(response.plan || 'free');
      setMonthlyUsed(response.monthly_used ?? 0);
      setMonthlyLimit(response.monthly_limit ?? 5);
    } catch (err) {
      console.error('Failed to load entitlements:', err);
      setError(err.message || 'Failed to load entitlements');
      setFeatures(GUEST_FEATURES);
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Check if a specific feature is enabled
   * 
   * @param {string} featureName - Feature name from Features enum
   * @returns {boolean} Whether feature is enabled
   */
  const isFeatureEnabled = useCallback((featureName) => {
    // If still loading, return false (fail-safe)
    if (loading) return false;
    
    // Return the feature value, defaulting to false if not found
    return features[featureName] === true;
  }, [features, loading]);

  /**
   * Refresh entitlements (call when tenant changes)
   */
  const refresh = useCallback(async () => {
    await loadEntitlements();
  }, [loadEntitlements]);

  // Load (or clear) entitlements whenever auth state settles
  useEffect(() => {
    if (authLoading) return;
    loadEntitlements();
  }, [authLoading, loadEntitlements]);

  const isProUser = plan === 'pro';

  const value = {
    features,
    loading,
    error,
    tenantId,
    plan,
    monthlyUsed,
    monthlyLimit,
    isProUser,
    isFeatureEnabled,
    refresh,
  };

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * useEntitlements Hook
 * 
 * Access tenant entitlements and feature flags.
 * 
 * @returns {Object} Entitlements state and helpers
 * @property {Object} features - Map of feature names to enabled status
 * @property {boolean} loading - Whether entitlements are still loading
 * @property {string|null} error - Error message if loading failed
 * @property {string|null} tenantId - Current tenant ID
 * @property {Function} isFeatureEnabled - Check if a feature is enabled
 * @property {Function} refresh - Reload entitlements from backend
 * 
 * @example
 * const { isFeatureEnabled, loading } = useEntitlements();
 * 
 * if (loading) return <Spinner />;
 * 
 * return (
 *   <>
 *     {isFeatureEnabled(Features.INVOICE_HISTORY) && <HistoryLink />}
 *     {isFeatureEnabled(Features.INVOICE_EXPORT_CSV) && <ExportButton />}
 *   </>
 * );
 */
export function useEntitlements() {
  const context = useContext(EntitlementsContext);
  
  if (context === undefined) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  
  return context;
}

// ============================================================================
// UTILITY COMPONENTS
// ============================================================================

/**
 * FeatureGate Component
 * 
 * Conditionally renders children based on feature flag.
 * 
 * @param {Object} props - Component props
 * @param {string} props.feature - Feature name to check
 * @param {React.ReactNode} props.children - Content to render if feature enabled
 * @param {React.ReactNode} [props.fallback] - Optional fallback if feature disabled
 * 
 * @example
 * <FeatureGate feature={Features.INVOICE_EXPORT_CSV}>
 *   <ExportButton />
 * </FeatureGate>
 * 
 * <FeatureGate 
 *   feature={Features.INVOICE_HISTORY} 
 *   fallback={<UpgradePrompt />}
 * >
 *   <HistoryPanel />
 * </FeatureGate>
 */
export function FeatureGate({ feature, children, fallback = null }) {
  const { isFeatureEnabled, loading } = useEntitlements();
  
  // Don't render anything while loading
  if (loading) return null;
  
  if (isFeatureEnabled(feature)) {
    return children;
  }
  
  return fallback;
}

const useEntitlementsExports = {
  EntitlementsProvider,
  useEntitlements,
  FeatureGate,
  Features,
};

export default useEntitlementsExports;
